export const BRAZIL_EVENT_TIMEZONES = [
  { value: "America/Noronha", label: "Horário de Fernando de Noronha", offset: "UTC-02:00" },
  { value: "America/Sao_Paulo", label: "Horário de Brasília", offset: "UTC-03:00" },
  { value: "America/Manaus", label: "Horário de Manaus", offset: "UTC-04:00" },
  { value: "America/Rio_Branco", label: "Horário do Acre", offset: "UTC-05:00" },
] as const;

export type BrazilEventTimeZone = (typeof BRAZIL_EVENT_TIMEZONES)[number]["value"];

export const DEFAULT_EVENT_TIME_ZONE: BrazilEventTimeZone = "America/Sao_Paulo";

const EVENT_TIME_ZONE_MAP = Object.fromEntries(
  BRAZIL_EVENT_TIMEZONES.map((timeZone) => [timeZone.value, timeZone]),
) as Record<BrazilEventTimeZone, (typeof BRAZIL_EVENT_TIMEZONES)[number]>;

const SUPPORTED_EVENT_TIME_ZONES = new Set<BrazilEventTimeZone>(BRAZIL_EVENT_TIMEZONES.map((timeZone) => timeZone.value));

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function parseDateTimeLocal(value: string): DateParts | null {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] || "0"),
  };
}

function datePartsToUtcMs(parts: DateParts) {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

function parseOffsetMinutes(rawOffset?: string) {
  if (!rawOffset) return null;

  const normalized = rawOffset.replace(/^UTC/i, "GMT");
  const match = normalized.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/i);
  if (!match) return null;

  const sign = match[1] === "+" ? 1 : -1;
  const hours = Number(match[2]);
  const minutes = Number(match[3] || "0");

  return sign * (hours * 60 + minutes);
}

function getTimeZoneParts(date: Date, timeZone: BrazilEventTimeZone): DateParts {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date).reduce<Record<string, string>>((accumulator, part) => {
    if (part.type !== "literal") {
      accumulator[part.type] = part.value;
    }
    return accumulator;
  }, {});

  const hour = Number(parts.hour || "0");

  return {
    year: Number(parts.year || "0"),
    month: Number(parts.month || "0"),
    day: Number(parts.day || "0"),
    hour: hour === 24 ? 0 : hour,
    minute: Number(parts.minute || "0"),
    second: Number(parts.second || "0"),
  };
}

function getTimeZoneOffsetMinutes(date: Date, timeZone: BrazilEventTimeZone) {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      timeZoneName: "shortOffset",
    });

    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find((part) => part.type === "timeZoneName")?.value;
    const parsedOffset = parseOffsetMinutes(offsetPart);
    if (parsedOffset !== null) return parsedOffset;
  } catch {
    // Fallback below.
  }

  const zonedParts = getTimeZoneParts(date, timeZone);
  return (datePartsToUtcMs(zonedParts) - date.getTime()) / 60000;
}

export function resolveEventTimeZone(value?: string | null): BrazilEventTimeZone {
  if (value && SUPPORTED_EVENT_TIME_ZONES.has(value as BrazilEventTimeZone)) {
    return value as BrazilEventTimeZone;
  }

  return DEFAULT_EVENT_TIME_ZONE;
}

export function getBrowserEventTimeZone(): BrazilEventTimeZone {
  const browserZone = typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : null;

  return resolveEventTimeZone(browserZone);
}

export function getEventTimeZoneLabel(value?: string | null): string {
  const timeZone = resolveEventTimeZone(value);
  const metadata = EVENT_TIME_ZONE_MAP[timeZone];
  return `${metadata.label} (${metadata.offset})`;
}

export function formatEventDate(value: Date | string, timeZone?: string | null, style: "long" | "short" = "long") {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  return new Intl.DateTimeFormat("pt-BR", style === "short"
    ? { timeZone: resolvedTimeZone, day: "2-digit", month: "2-digit", year: "numeric" }
    : { timeZone: resolvedTimeZone, day: "2-digit", month: "long", year: "numeric" }
  ).format(new Date(value));
}

export function formatEventWeekday(value: Date | string, timeZone?: string | null) {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: resolvedTimeZone,
    weekday: "long",
  }).format(new Date(value));
}

export function formatEventTime(value: Date | string, timeZone?: string | null) {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: resolvedTimeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

export function formatEventDateInputValue(value: Date | string, timeZone?: string | null) {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const parts = getTimeZoneParts(new Date(value), resolvedTimeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function convertIsoToLocalDateTime(value?: string | null, timeZone?: string | null) {
  if (!value) return "";

  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const parts = getTimeZoneParts(new Date(value), resolvedTimeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function convertLocalDateTimeToIso(value?: string | null, timeZone?: string | null) {
  if (!value) return null;

  const localParts = parseDateTimeLocal(value);
  if (!localParts) return null;

  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const desiredUtcMs = datePartsToUtcMs(localParts);
  let utcMs = desiredUtcMs;

  for (let i = 0; i < 3; i += 1) {
    const offsetMinutes = getTimeZoneOffsetMinutes(new Date(utcMs), resolvedTimeZone);
    const nextUtcMs = desiredUtcMs - (offsetMinutes * 60 * 1000);
    if (nextUtcMs === utcMs) break;
    utcMs = nextUtcMs;
  }

  return new Date(utcMs).toISOString();
}

export function formatEventDateTimeRange(
  startDate: Date | string,
  endDate?: Date | string | null,
  timeZone?: string | null,
  options?: {
    dateStyle?: "long" | "short";
    includeZoneLabel?: boolean;
    dateSeparator?: string;
    timeSeparator?: string;
    betweenDatesSeparator?: string;
  },
) {
  const resolvedTimeZone = resolveEventTimeZone(timeZone);
  const dateStyle = options?.dateStyle || "long";
  const includeZoneLabel = options?.includeZoneLabel !== false;
  const dateSeparator = options?.dateSeparator || ", ";
  const timeSeparator = options?.timeSeparator || " — ";
  const betweenDatesSeparator = options?.betweenDatesSeparator || " • ";

  const startDateText = formatEventDate(startDate, resolvedTimeZone, dateStyle);
  const startTimeText = formatEventTime(startDate, resolvedTimeZone);
  const endTimeText = endDate ? formatEventTime(endDate, resolvedTimeZone) : "";

  // Evento que atravessa dias precisa mostrar a data final tambem, senao o
  // publico so ve o primeiro dia.
  const spansMultipleDays = Boolean(endDate)
    && formatEventDateInputValue(startDate, resolvedTimeZone) !== formatEventDateInputValue(endDate!, resolvedTimeZone);

  const scheduleText = spansMultipleDays
    ? `${startDateText}${dateSeparator}${startTimeText}${timeSeparator}${formatEventDate(endDate!, resolvedTimeZone, dateStyle)}${dateSeparator}${endTimeText}`
    : `${startDateText}${dateSeparator}${endTimeText ? `${startTimeText}${timeSeparator}${endTimeText}` : startTimeText}`;

  return [
    scheduleText,
    includeZoneLabel ? getEventTimeZoneLabel(resolvedTimeZone) : null,
  ].filter(Boolean).join(betweenDatesSeparator);
}
